import React, { useState, useEffect } from 'react';
import { QrCode, Pencil, ExternalLink, Check, X, Trash2, AlertTriangle, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { apiFetch } from './api';
import LinkCard from './LinkCard';
import { btnClass, ErrorBanner } from './ui';

// Destructive styling: the trigger stays quiet so it does not compete with Edit,
// and only the final confirm button goes solid red.
const dangerGhost = 'text-sm font-semibold py-2 px-3 rounded-md flex items-center justify-center transition-colors duration-200 cursor-pointer text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500';
const dangerSolid = 'text-sm font-semibold py-2 px-4 rounded-md flex items-center justify-center transition-colors duration-200 cursor-pointer bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300 dark:disabled:bg-red-900/50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500';

// Client-side: 66 links is ~30KB of JSON that already arrives in one response,
// so paging on the server would add a round trip per page for nothing.
// ponytail: move to ?page=&limit= if this ever passes a few thousand links.
const PAGE_SIZE = 10;

// 44px square on phones so the arrows are actually tappable.
const pagerBtn = 'inline-flex items-center justify-center min-h-[44px] min-w-[44px] sm:min-h-[36px] sm:min-w-[36px] rounded-md border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 cursor-pointer transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500';

export default function LinksPage() {
    const [links, setLinks] = useState(null);
    const [error, setError] = useState('');
    const [openId, setOpenId] = useState(null);
    const [editId, setEditId] = useState(null);
    const [draft, setDraft] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [confirmText, setConfirmText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [page, setPage] = useState(1);
    const [query, setQuery] = useState('');

    useEffect(() => {
        apiFetch('/api/links')
            .then(setLinks)
            .catch((err) => { setError(err.message); setLinks([]); });
    }, []);

    const startEdit = (link) => { setEditId(link.id); setDraft(link.longUrl); setError(''); };

    const save = async (id) => {
        setIsSaving(true);
        setError('');
        try {
            // Only longUrl is sent. The short code cannot change, so every QR
            // code already printed for this link keeps working.
            const updated = await apiFetch(`/api/links/${id}`, {
                method: 'PATCH',
                body: JSON.stringify({ longUrl: draft }),
            });
            setLinks((prev) => prev.map((l) => (l.id === id ? { ...l, longUrl: updated.longUrl } : l)));
            setEditId(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const startDelete = (link) => {
        setDeleteId(link.id);
        setConfirmText('');
        setEditId(null);
        setOpenId(null);
        setError('');
    };

    const confirmDelete = async (link) => {
        setIsDeleting(true);
        setError('');
        try {
            await apiFetch(`/api/links/${link.id}`, { method: 'DELETE' });
            setLinks((prev) => prev.filter((l) => l.id !== link.id));
            setDeleteId(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsDeleting(false);
        }
    };

    if (links === null) {
        return <p className="text-center text-gray-500 dark:text-gray-400">Loading your links…</p>;
    }

    // Filtering 66 rows on every keystroke is well under a frame, so no debounce
    // and no memo — both would be machinery guarding a cost that isn't there.
    const q = query.trim().toLowerCase();
    const filtered = q
        ? links.filter((l) => l.shortCode.toLowerCase().includes(q) || l.longUrl.toLowerCase().includes(q))
        : links;

    // safePage is derived, not stored: deleting the last row on the final page,
    // or narrowing the search, clamps back into range on its own.
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const firstIndex = (safePage - 1) * PAGE_SIZE;
    const visible = filtered.slice(firstIndex, firstIndex + PAGE_SIZE);

    // A new query invalidates the page you were on, and any half-finished edit
    // or delete belongs to a row that may be about to scroll out of view.
    const onSearch = (value) => {
        setQuery(value);
        setPage(1);
        setDeleteId(null);
        setEditId(null);
        setOpenId(null);
    };

    const goTo = (next) => {
        setPage(next);
        setDeleteId(null);
        setEditId(null);
        setOpenId(null);
        // Respect the OS "reduce motion" setting rather than always animating.
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    };

    return (
        <>
            <h1 className="text-3xl font-bold text-gray-800 dark:text-white text-center mb-8">My links</h1>
            <div className="mb-4"><ErrorBanner message={error} /></div>

            {links.length > 0 && (
                <div className="mb-4">
                    <label htmlFor="link-search" className="sr-only">Search your links</label>
                    <div className="relative">
                        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
                        <input
                            id="link-search"
                            type="search"
                            value={query}
                            onChange={(e) => onSearch(e.target.value)}
                            placeholder="Search by short code or destination"
                            // Hide WebKit's built-in clear widget; we render our own
                            // so it looks the same in every browser.
                            className="w-full min-h-[44px] bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg py-2.5 pl-10 pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-200 [&::-webkit-search-cancel-button]:appearance-none"
                        />
                        {query && (
                            <button
                                onClick={() => onSearch('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                                aria-label="Clear search"
                            >
                                <X size={16} aria-hidden="true" />
                            </button>
                        )}
                    </div>
                    {q && (
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400" aria-live="polite">
                            {filtered.length} of {links.length} links match
                        </p>
                    )}
                </div>
            )}

            {links.length === 0 && (
                <p className="text-center text-gray-500 dark:text-gray-400">No links yet.</p>
            )}

            {links.length > 0 && filtered.length === 0 && (
                <p className="text-center text-gray-500 dark:text-gray-400 py-6">
                    Nothing matches “{query}”.
                </p>
            )}

            <ul className="space-y-3">
                {visible.map((link) => (
                    <li key={link.id} className="bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex flex-wrap items-center gap-3">
                            <a href={link.shortUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-green-400 font-medium hover:underline break-all">
                                /{link.shortCode}
                            </a>
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
                                {new Date(link.createdAt).toLocaleDateString()}
                            </span>
                        </div>

                        {editId === link.id ? (
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                                <input
                                    type="url"
                                    value={draft}
                                    onChange={(e) => setDraft(e.target.value)}
                                    className="flex-1 min-w-[16rem] bg-white dark:bg-gray-900 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    disabled={isSaving}
                                    autoFocus
                                />
                                <button onClick={() => save(link.id)} className={btnClass(false)} disabled={isSaving || !draft}>
                                    <Check size={16} className="mr-2" />Save
                                </button>
                                <button onClick={() => setEditId(null)} className={btnClass(false)} disabled={isSaving}>
                                    <X size={16} className="mr-2" />Cancel
                                </button>
                            </div>
                        ) : deleteId === link.id ? (
                            <div className="mt-3 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/30 p-3">
                                <div className="flex items-start gap-2 text-sm text-red-800 dark:text-red-200">
                                    <AlertTriangle size={18} className="shrink-0 mt-0.5" aria-hidden="true" />
                                    <p>
                                        This is permanent. Any QR code already printed for{' '}
                                        <span className="font-semibold">/{link.shortCode}</span> stops working,
                                        and the name becomes free for someone else to take.
                                    </p>
                                </div>

                                <label htmlFor={`confirm-${link.id}`} className="mt-3 block text-sm text-red-800 dark:text-red-200">
                                    Type <span className="font-semibold">{link.shortCode}</span> to confirm:
                                </label>
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                    <input
                                        id={`confirm-${link.id}`}
                                        type="text"
                                        value={confirmText}
                                        onChange={(e) => setConfirmText(e.target.value)}
                                        className="flex-1 min-w-[12rem] bg-white dark:bg-gray-900 text-gray-900 dark:text-white border border-red-300 dark:border-red-800 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-red-500"
                                        autoComplete="off"
                                        autoFocus
                                        disabled={isDeleting}
                                    />
                                    <button
                                        onClick={() => confirmDelete(link)}
                                        className={dangerSolid}
                                        disabled={isDeleting || confirmText !== link.shortCode}
                                    >
                                        <Trash2 size={16} className="mr-2" aria-hidden="true" />
                                        {isDeleting ? 'Deleting…' : 'Delete'}
                                    </button>
                                    <button onClick={() => setDeleteId(null)} className={btnClass(false)} disabled={isDeleting}>
                                        <X size={16} className="mr-2" aria-hidden="true" />Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="mt-2 flex flex-wrap items-center gap-3">
                                <a href={link.longUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-gray-600 dark:text-gray-300 hover:underline break-all flex-1 min-w-0">
                                    <ExternalLink size={14} className="inline mr-1" />{link.longUrl}
                                </a>
                                <button onClick={() => startEdit(link)} className={btnClass(false)}>
                                    <Pencil size={16} className="mr-2" aria-hidden="true" />Edit
                                </button>
                                <button onClick={() => setOpenId(openId === link.id ? null : link.id)} className={btnClass(openId === link.id)}>
                                    <QrCode size={16} className="mr-2" aria-hidden="true" />QR
                                </button>
                                <button onClick={() => startDelete(link)} className={dangerGhost} aria-label={`Delete /${link.shortCode}`}>
                                    <Trash2 size={16} aria-hidden="true" />
                                </button>
                            </div>
                        )}

                        {/* One card mounted at a time. Each holds a hidden 512px canvas,
                            and 66 of those would be tens of MB of backing store. */}
                        {openId === link.id && (
                            <div className="mt-4"><LinkCard shortUrl={link.shortUrl} onError={setError} /></div>
                        )}
                    </li>
                ))}
            </ul>

            {totalPages > 1 && (
                <nav aria-label="Pagination" className="mt-6 flex items-center justify-between gap-3">
                    <p className="text-sm text-gray-500 dark:text-gray-400" aria-live="polite">
                        {firstIndex + 1}–{firstIndex + visible.length} of {links.length}
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => goTo(safePage - 1)}
                            className={pagerBtn}
                            disabled={safePage === 1}
                            aria-label="Previous page"
                        >
                            <ChevronLeft size={16} aria-hidden="true" />
                        </button>
                        <span className="text-sm text-gray-600 dark:text-gray-300 tabular-nums">
                            {safePage} / {totalPages}
                        </span>
                        <button
                            onClick={() => goTo(safePage + 1)}
                            className={pagerBtn}
                            disabled={safePage === totalPages}
                            aria-label="Next page"
                        >
                            <ChevronRight size={16} aria-hidden="true" />
                        </button>
                    </div>
                </nav>
            )}
        </>
    );
}
